import {
  REFERRAL_SOURCE_OPTIONS,
  isPersonBasedReferralSource,
} from '../../constants/referralSources';
import { TenantListboxPicker } from './TenantListboxPicker';

function listboxTriggerClass(fieldClass: string): string {
  return `${fieldClass} flex items-center justify-between gap-2 text-left transition-colors hover:border-gray-400 dark:hover:border-white/20`;
}

interface ReferralCaptureFieldsProps {
  referrerName: string;
  referrerSource: string;
  onReferrerNameChange: (value: string) => void;
  onReferrerSourceChange: (value: string) => void;
  fieldClass: string;
  compactColumn?: boolean;
  /** Cap control width (e.g. `max-w-sm`) while staying responsive inside narrow layouts. */
  fieldMaxWidthClass?: string;
  nameId?: string;
  sourceId?: string;
}

export function ReferralCaptureFields({
  referrerName,
  referrerSource,
  onReferrerNameChange,
  onReferrerSourceChange,
  fieldClass,
  compactColumn = false,
  fieldMaxWidthClass,
  nameId = 'referral-name',
  sourceId = 'referral-source',
}: ReferralCaptureFieldsProps) {
  const showReferrerName = isPersonBasedReferralSource(referrerSource);
  const showNonGiftNote = Boolean(referrerSource.trim()) && !showReferrerName;
  const fieldWrapClass =
    fieldMaxWidthClass ?? (compactColumn ? 'min-w-0 w-full' : 'max-w-xl');
  const subsectionHeadingClass = `block font-semibold text-gray-900 dark:text-white ${
    compactColumn ? 'mb-1.5 text-sm' : 'mb-2 text-base'
  }`;

  return (
    <div className={compactColumn ? 'space-y-3' : 'space-y-4'}>
      <div className={fieldWrapClass}>
        <label htmlFor={sourceId} className={subsectionHeadingClass}>
          How did you hear about us?
        </label>
        <TenantListboxPicker
          id={sourceId}
          value={referrerSource}
          placeholder="Select one"
          options={REFERRAL_SOURCE_OPTIONS.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          triggerClassName={listboxTriggerClass(fieldClass)}
          onChange={(next) => {
            onReferrerSourceChange(next);
            if (!isPersonBasedReferralSource(next)) {
              onReferrerNameChange('');
            }
          }}
        />
        {showNonGiftNote ? (
          <p className={`mt-1.5 text-gray-500 dark:text-gray-400 ${compactColumn ? 'text-xs' : 'text-sm'}`}>
            Thanks — this helps us understand marketing. It does not qualify for the tenant referral thank-you.
          </p>
        ) : null}
      </div>
      {showReferrerName ? (
        <div className={fieldWrapClass}>
          <label htmlFor={nameId} className={subsectionHeadingClass}>
            Which tenant referred you?
          </label>
          <input
            id={nameId}
            type="text"
            value={referrerName}
            onChange={(event) => onReferrerNameChange(event.target.value)}
            autoComplete="off"
            placeholder="Their name, as you know it"
            className={fieldClass}
          />
        </div>
      ) : null}
    </div>
  );
}
